/**
 * Alters-Auswahl + rechtliches Warnfeld — zentrale Bausteine für
 * News, Popups und Newsletter. Semantik strikt technisch: die App
 * behauptet nirgends „18+ = Werbung erlaubt". Der Warntext bezieht
 * sich ausdrücklich auf DE-Rechtsgrundlagen und erinnert daran, dass
 * Altersfilter KEINE Rechtsprüfung ersetzen.
 */
import React from "react";
import { AlertTriangle } from "lucide-react";
import { Label } from "./ui/label";

export const AGE_OPTIONS = [
  { value: 0,  label: "Ohne Beschränkung" },
  { value: 16, label: "Ab 16 Jahren" },
  { value: 18, label: "Ab 18 Jahren" },
  { value: 21, label: "Ab 21 Jahren" },
];

/**
 * Kleines Select für die Altersstufe. Der Wert ist ein Int (0/16/18/21).
 * ``testId`` wird auf das <select> gesetzt, damit die E2E-Tests robust
 * treffen können.
 */
export function AgeSelect({ value, onChange, testId, disabled }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-zinc-400">Altersfreigabe</Label>
      <select
        data-testid={testId}
        value={Number(value ?? 0)}
        disabled={disabled}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="mt-1 h-10 w-full bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-zinc-100 px-2 disabled:opacity-60"
      >
        {AGE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <div className="text-[11px] text-zinc-500 mt-1">
        Kunden ohne gepflegtes Geburtsdatum sehen Inhalte mit Altersfreigabe nicht (fail closed).
      </div>
    </div>
  );
}

/**
 * Nicht-wegklickbares rotes Warnfeld für Content-Editoren. Bewusst
 * kompakt gehalten und ohne Alarmismus. Bleibt permanent sichtbar,
 * damit der Redakteur beim Anlegen/Bearbeiten daran erinnert wird,
 * die rechtlichen Vorgaben selbst zu prüfen.
 */
export function ComplianceWarning({ testId }) {
  return (
    <div
      data-testid={testId || "compliance-warning"}
      role="note"
      className="text-xs leading-relaxed p-3 rounded-sm border border-red-500/60 bg-red-500/10 text-red-300 dark:text-red-300 flex gap-2 items-start"
    >
      <AlertTriangle size={14} className="shrink-0 mt-0.5 text-red-400" />
      <div className="space-y-1">
        <div className="font-semibold uppercase tracking-wider text-red-300">
          Rechtlicher Hinweis (DE)
        </div>
        <div>
          <strong>Alkohol:</strong> Jugendschutz- und Werbevorgaben beachten. Abgabe je nach
          Produkt erst ab 16 bzw. 18 Jahren (§ 9 JuSchG).
        </div>
        <div>
          <strong>Tabak / Vape:</strong> Werbung für Tabakerzeugnisse, E-Zigaretten und
          Nachfüllbehälter ist in Diensten der Informationsgesellschaft grundsätzlich
          verboten (§ 19 TabakerzG).
        </div>
        <div className="opacity-90">
          Altersfilter ersetzen keine rechtliche Prüfung.
        </div>
      </div>
    </div>
  );
}
